import React, { useState, useMemo } from 'react';
import { getLocaleDateTimeString, history, convertCentsToMoney } from '../../../utils';
import { IRental } from '../../../ts/interface/rental.interface';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import { IColumn } from '../../../ts/interface/components/redesing/table.interface';
import AddIcon from '@mui/icons-material/Add';
import { getHardwareIconByName } from '../../../util/utilsR';
import ColumnAvatar from '../../../components/Redesing/column-avatar';
import Typography from '@mui/material/Typography';
import { SeverityPill } from '../../../components/Redesing/severity-pill';
import { endByColumn } from '../../../tableColumns/rental-columns';
import Card from '@mui/material/Card';

interface UserRentalsProps {
  rentals: Partial<IRental>[];
  userPhone: string;
  searchText: string;
  onSearchChange: (search: string) => void;
  noRentalsText?: string;
  onAddRental?: () => void;
  isLoading?: boolean;
  isFetching?: boolean;
}

const UserRentals = ({
  rentals,
  userPhone,
  searchText,
  onSearchChange,
  noRentalsText = 'This user does not have any rentals',
  onAddRental,
  isLoading = false,
  isFetching = false,
}: UserRentalsProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('startTime');

  const columns: IColumn[] = useMemo(
    () => [
      {
        id: 'hardwareNumber',
        label: 'Hardware',
        notSortable: true,
        format: (row: Partial<IRental>) => {
          const hardware = row.hardware;
          if (!hardware) return ' ';
          const identifierName = hardware.number ? 'Number' : 'ID';
          const { Icon, bgColor } = getHardwareIconByName(hardware.type_name || '');
          return (
            <ColumnAvatar
              avatar={<Icon />}
              avatarColor={bgColor}
              firstRow={
                <Typography color="text.primary" variant="body1">
                  {hardware.number || hardware.id}
                </Typography>
              }
              secondRow={
                <Typography color="text.secondary" variant="body2">
                  <SeverityPill color={'info'}>{identifierName}</SeverityPill>
                </Typography>
              }
            />
          );
        },
      },
      {
        id: 'startStationName',
        label: 'Start Location',
        notSortable: true,
        format: (row: Partial<IRental>) => row.start?.station?.name || ' ',
      },
      {
        id: 'startTime',
        label: 'Start Time',
        format: (row: Partial<IRental>) =>
          row.start?.time ? getLocaleDateTimeString(row.start.time) : ' ',
      },
      {
        id: 'endStationName',
        label: 'End Location',
        notSortable: true,
        format: (row: Partial<IRental>) => row.end?.station?.name || ' ',
      },
      {
        id: 'endTime',
        label: 'End Time',
        notSortable: true,
        format: (row: Partial<IRental>) =>
          row.end?.time ? getLocaleDateTimeString(row.end.time) : ' ',
      },
      endByColumn(),
      {
        id: 'billing.total',
        label: 'Total',
        notSortable: true,
        format: (row: Partial<IRental>) => convertCentsToMoney(row.billing?.total || 0),
      },
    ],
    []
  );

  return (
    <Card>
      <PaginationTable
        isLoading={isLoading}
        isLoadingCache={isFetching}
        localSearch={false}
        noRemoteSort
        disableHasMore
        onRowCLick={(id: string) => history.push(`/rentals/${id}`)}
        handleSearch={(e) => {
          setPage(0);
          onSearchChange(e.target.value);
        }}
        searchText={searchText}
        sortBy={sortBy}
        onSortChange={(sort) => setSortBy(sort.key)}
        columns={columns}
        showFilter={false}
        items={rentals}
        buttons={[
          {
            startIcon: <AddIcon />,
            label: 'Add',
            onClick: () => onAddRental && onAddRental(),
          },
        ]}
        onPageChange={(_, pageN) => setPage(pageN)}
        onRowsPerPageChange={(row) => {
          const pageSize = Number(row.target.value);
          setRowsPerPage(pageSize);
          setPage(0);
        }}
        page={page}
        rowsPerPage={rowsPerPage}
        searchPlaceholder={'Search rentals'}
      />
    </Card>
  );
};

export default UserRentals;
